import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import './AdminQuizDashboard.css';

// Configure axios base URL
axios.defaults.baseURL = API_BASE_URL;

const AdminQuizDashboard = ({ userRole, onBack }) => {
  const [stats, setStats] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    subject: '',
    class: '',
    difficulty: ''
  });
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/quiz/admin-stats');
      setStats({
        totalQuestions: response.data.totalQuestions || 0,
        totalSubjects: response.data.totalSubjects || 0,
        totalClasses: response.data.totalClasses || 0,
        questionsBySubject: response.data.questionsBySubject || [],
        questionsByClass: response.data.questionsByClass || [],
        questionsByDifficulty: response.data.questionsByDifficulty || [],
        recentScores: response.data.recentScores || []
      });
      setSubjects(Array.isArray(response.data.subjects) ? response.data.subjects : []);
      setClasses(Array.isArray(response.data.classes) ? response.data.classes : []);
      console.log('Admin stats loaded:', response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default values on error
      setStats({ totalQuestions: 0, totalSubjects: 0, totalClasses: 0, recentScores: [] });
      setSubjects([]);
      setClasses([]);
    }
  };

  const fetchSubjects = async () => {
    // Subjects are fetched in fetchStats, no need for separate call
  };

  const fetchClasses = async () => {
    // Classes are fetched in fetchStats, no need for separate call
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/quiz/questions/manage', { 
        params: selectedFilters 
      });
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async (questionIds) => {
    if (window.confirm(`Delete ${questionIds.length} questions?`)) {
      try {
        await Promise.all(
          questionIds.map(id => axios.delete(`/api/quiz/questions/${id}`))
        );
        fetchQuestions();
        alert('Questions deleted successfully!');
      } catch (error) {
        alert('Error deleting questions: ' + error.message);
      }
    }
  };

  if (!['admin1', 'admin', 'teacher_admin_level1'].includes(userRole)) {
    return (
      <div className="access-denied">
        <h2>🚫 Access Denied</h2>
        <p>Admin Level 1 privileges required</p>
        <button onClick={onBack}>Back</button>
      </div>
    );
  }

  return (
    <div className="admin-quiz-dashboard">
      <div className="dashboard-header">
        <h2>⚡ Admin Level 1 Quiz Control</h2>
        <button onClick={onBack} className="back-btn">← Back</button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-info">
            <h3>{stats.totalQuestions || 0}</h3>
            <p>Total Questions</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-info">
            <h3>{stats.totalSubjects || 0}</h3>
            <p>Subjects</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏫</div>
          <div className="stat-info">
            <h3>{stats.totalClasses || 0}</h3>
            <p>Classes</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎯</div>
          <div className="stat-info">
            <h3>{stats.recentScores?.length || 0}</h3>
            <p>Recent Scores</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>🎮 Manage Quiz Games</h3>
        <div className="action-buttons">
          <button className="action-btn primary">
            📤 Bulk Upload Questions
          </button>
          <button className="action-btn secondary">
            📊 Monitor Performance
          </button>
          <button className="action-btn success">
            🎯 Create Quiz Sets
          </button>
          <button className="action-btn warning">
            ⚙️ Configure Difficulty
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <h3>Filter Questions</h3>
        <div className="filters">
          <select 
            value={selectedFilters.subject}
            onChange={(e) => setSelectedFilters({...selectedFilters, subject: e.target.value})}
          >
            <option value="">All Subjects</option>
            {Array.isArray(subjects) && subjects.map(subject => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>

          <select 
            value={selectedFilters.class}
            onChange={(e) => setSelectedFilters({...selectedFilters, class: e.target.value})}
          >
            <option value="">All Classes</option>
            {Array.from({length: 12}, (_, i) => (
              <option key={i+1} value={i+1}>Class {i+1}</option>
            ))}
          </select>

          <select 
            value={selectedFilters.difficulty}
            onChange={(e) => setSelectedFilters({...selectedFilters, difficulty: e.target.value})}
          >
            <option value="">All Difficulties</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>

          <button onClick={fetchQuestions} className="filter-btn">
            🔍 Search
          </button>
        </div>
      </div>

      {/* Questions List */}
      {loading ? (
        <div className="loading">Loading questions...</div>
      ) : (
        <div className="questions-section">
          <div className="section-header">
            <h3>Questions ({questions.length})</h3>
            {questions.length > 0 && (
              <button 
                onClick={() => handleBulkDelete(questions.map(q => q._id))}
                className="bulk-delete-btn"
              >
                🗑️ Delete All Filtered
              </button>
            )}
          </div>
          
          <div className="questions-grid">
            {questions.map(question => (
              <div key={question._id} className="question-preview">
                <div className="question-header">
                  <span className="subject-tag">{question.subject}</span>
                  <span className="class-tag">Class {question.class}</span>
                  <span className={`difficulty-tag ${question.difficulty}`}>
                    {question.difficulty}
                  </span>
                </div>
                <div className="question-text">
                  {question.question.substring(0, 100)}...
                </div>
                <div className="question-meta">
                  <span>Chapter: {question.chapter}</span>
                  <span>Options: {question.options.length}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuizDashboard;